/**
 * useGoalAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 3 ("Goal").
 * Mirrors useYogAnalytics.js / useWelcomeAnalytics.js — same GA4 wrapper via
 * react-ga4, same IS_PRODUCTION guard, same dev console.group/table logging.
 *
 * Privacy (Nutrition Policy v1.0 §19 Privacy & Data Minimization): raw
 * health answers (weight, target weight) must never be sent to ordinary
 * analytics events. trackTargetWeightWarningShown sends only a boolean —
 * never the underlying kg values.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[GoalAnalytics] ${eventName}`, "color:#2E7D32;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useGoalAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("goal_step_view", { session_id: sessionId, step: 3 });
  }, []);

  const trackGoalSelect = useCallback((sessionId, goalId, goalLabel) => {
    send("goal_select", {
      session_id: sessionId,
      goal_id: goalId,
      goal_label: goalLabel,
    });
  }, []);

  const trackStepNext = useCallback((sessionId, goalId) => {
    send("goal_step_next", { session_id: sessionId, goal_id: goalId, step: 3 });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("goal_step_previous", { session_id: sessionId, step: 3 });
  }, []);

  /**
   * @param {string} sessionId
   * @param {object} [meta]
   * @param {boolean} [meta.shown] - Always true when called; kept as an
   *   explicit field (rather than sending no params) for GA event
   *   consistency. Deliberately does NOT accept weightKg/targetWeightKg —
   *   raw health answers must never reach analytics.
   */
  const trackTargetWeightWarningShown = useCallback((sessionId, { shown = true } = {}) => {
    send("goal_target_weight_warning_shown", {
      session_id: sessionId,
      shown,
    });
  }, []);

  return {
    trackStepView,
    trackGoalSelect,
    trackStepNext,
    trackStepPrevious,
    trackTargetWeightWarningShown,
  };
};

export default useGoalAnalytics;